import React from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import RebuildMapCanvas from "./RebuildMapCanvas";
import RebuildNavigationPanels from "./RebuildNavigationPanels";
import NavigationReadoutCards from "./NavigationReadoutCards";
import NorthCompass from "./NorthCompass";
import RoutePlannerWidget from "./RoutePlannerWidget";
import GpsSignalIndicator from "./GpsSignalIndicator";
import { DEFAULT_LAYERS } from "./navigationRebuildUtils";

const EARTH_RADIUS_NM = 3440.065;
const GPS_STALE_MS = 12000;
const MAX_USABLE_ACCURACY_METERS = 150;

function signalStrengthFromAccuracy(accuracy) {
  if (!Number.isFinite(accuracy)) return 0;
  if (accuracy <= 10) return 4;
  if (accuracy <= 30) return 3;
  if (accuracy <= 75) return 2;
  return 1;
}

function isBetterGpsFix(nextFix, lastFix) {
  if (!lastFix) return Array.isArray(nextFix.point);
  const nextIsFresh = nextFix.recordedAt >= lastFix.recordedAt;
  const muchWorse = nextFix.accuracy > Math.max(lastFix.accuracy * 2.5, lastFix.accuracy + 50);
  return nextIsFresh && (!muchWorse || nextFix.accuracy <= MAX_USABLE_ACCURACY_METERS);
}

function smoothPoint(previousPoint, nextPoint, accuracy) {
  if (!previousPoint) return nextPoint;
  const weight = accuracy <= 10 ? 0.9 : accuracy <= 30 ? 0.7 : 0.5;
  return [
    previousPoint[0] * (1 - weight) + nextPoint[0] * weight,
    previousPoint[1] * (1 - weight) + nextPoint[1] * weight,
  ];
}

function estimatePointFromFix(fix) {
  if (!fix?.point || !Number.isFinite(fix.heading) || !Number.isFinite(fix.speedKnots)) return fix?.point;
  const elapsedHours = Math.max(0, Date.now() - fix.recordedAt) / 3600000;
  const distanceNm = fix.speedKnots * elapsedHours;
  if (distanceNm <= 0) return fix.point;

  const lat1 = (fix.point[0] * Math.PI) / 180;
  const lon1 = (fix.point[1] * Math.PI) / 180;
  const bearing = (fix.heading * Math.PI) / 180;
  const angularDistance = distanceNm / EARTH_RADIUS_NM;
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(angularDistance) + Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearing));
  const lon2 = lon1 + Math.atan2(Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat1), Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2));

  return [(lat2 * 180) / Math.PI, (((lon2 * 180) / Math.PI + 540) % 360) - 180];
}

export default function RebuildNavigationMap() {
  const [layers, setLayers] = React.useState(DEFAULT_LAYERS);
  const [marinas, setMarinas] = React.useState([]);
  const [routePoints, setRoutePoints] = React.useState([]);
  const [cursor, setCursor] = React.useState(null);
  const [telemetry, setTelemetry] = React.useState(null);
  const [vesselTrace, setVesselTrace] = React.useState([]);
  const [isFullScreen, setIsFullScreen] = React.useState(false);
  const [centerRequestKey, setCenterRequestKey] = React.useState(0);
  const lastFixRef = React.useRef(null);

  React.useEffect(() => {
    base44.entities.Marina.list("-rating", 120).then((records) => {
      setMarinas(records.filter((item) => typeof item.latitude === "number" && typeof item.longitude === "number"));
    });
  }, []);

  React.useEffect(() => {
    if (!navigator.geolocation) return;

    const showEstimatedLocation = () => {
      const lastFix = lastFixRef.current;
      if (!lastFix || Date.now() - lastFix.recordedAt < GPS_STALE_MS) return;
      setTelemetry({
        ...lastFix,
        point: estimatePointFromFix(lastFix),
        isEstimated: true,
        gpsStatus: "estimated",
        signalStrength: 1,
        timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      });
    };

    const watcher = navigator.geolocation.watchPosition(
      (position) => {
        const rawPoint = [position.coords.latitude, position.coords.longitude];
        const accuracy = position.coords.accuracy || 999;
        const recordedAt = position.timestamp || Date.now();
        const lastFix = lastFixRef.current;
        const nextFix = {
          point: smoothPoint(lastFix?.isEstimated ? null : lastFix?.point, rawPoint, accuracy),
          accuracy,
          heading: Number.isFinite(position.coords.heading) ? position.coords.heading : (lastFix?.heading ?? null),
          speedKnots: Number.isFinite(position.coords.speed) ? Math.max(0, position.coords.speed * 1.94384) : (lastFix?.speedKnots ?? 0),
          timestamp: new Date(recordedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          recordedAt,
          isEstimated: false,
          gpsStatus: "live",
          signalStrength: signalStrengthFromAccuracy(accuracy),
        };

        if (!isBetterGpsFix(nextFix, lastFix)) return;

        lastFixRef.current = nextFix;
        setTelemetry(nextFix);
        setVesselTrace((current) => {
          const last = current[current.length - 1];
          if (last && Math.abs(last[0] - nextFix.point[0]) < 0.00001 && Math.abs(last[1] - nextFix.point[1]) < 0.00001) return current;
          return [...current, nextFix.point].slice(-180);
        });
      },
      showEstimatedLocation,
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 }
    );

    const staleCheck = window.setInterval(showEstimatedLocation, 2000);
    return () => {
      navigator.geolocation.clearWatch(watcher);
      window.clearInterval(staleCheck);
    };
  }, []);

  return (
    <section className={`${isFullScreen ? "fixed inset-0 z-[9999] h-dvh min-h-dvh" : "relative h-[calc(100dvh-5rem)] min-h-[620px]"} overflow-hidden bg-sky-100 dark:bg-slate-950`}>
      <RebuildMapCanvas layers={layers} marinas={marinas} routePoints={routePoints} telemetry={telemetry} vesselTrace={vesselTrace} centerRequestKey={centerRequestKey} onCursor={setCursor} onAddWaypoint={(point) => setRoutePoints((points) => [...points, point])} onDeleteWaypoint={(index) => setRoutePoints((points) => points.filter((_, pointIndex) => pointIndex !== index))} />
      <RebuildNavigationPanels cursor={cursor} routePoints={routePoints} userPosition={telemetry?.point} onClearRoute={() => setRoutePoints([])} />
      <NavigationReadoutCards telemetry={telemetry} routePoints={routePoints} />
      <NorthCompass heading={telemetry?.heading} />
      <RoutePlannerWidget cursor={cursor} position={telemetry?.point} routePoints={routePoints} onAddWaypoint={(point) => setRoutePoints((points) => [...points, point])} onClearRoute={() => setRoutePoints([])} onEnableRoute={() => setLayers((current) => ({ ...current, route: true, waypoints: true }))} onLoadRoute={(points) => { setRoutePoints(points); setLayers((current) => ({ ...current, route: true, waypoints: true })); }} />
      <GpsSignalIndicator telemetry={telemetry} onCenter={() => setCenterRequestKey(Date.now())} />
      <Button
        type="button"
        size="icon"
        aria-label={isFullScreen ? "Exit full page map" : "Open map full page"}
        onClick={() => setIsFullScreen((value) => !value)}
        className="absolute right-3 top-[10.75rem] z-[950] h-11 w-11 border border-white/70 bg-white/95 text-primary shadow-xl shadow-black/20 hover:bg-white dark:border-cyan-100/20 dark:bg-slate-950/95 dark:text-white dark:hover:bg-white/10 md:right-5 md:top-56 md:h-12 md:w-12"
      >
        {isFullScreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
      </Button>


    </section>
  );
}